import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";
import {
  MessageSquare,
  Search,
  Send,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Bell,
  Inbox,
  Clock3,
  UserCircle2,
  Building2,
  ChevronRight,
  X,
  Trash2,
  ArrowDown,
  Info,
  Home,
} from "lucide-react";
import "./HomebuyerCommunicationPage.css";
import {
  fetchCommunicationThreads,
  fetchDossierCommunicationThreads,
  fetchCommunicationThreadDetail,
  createThreadMessage,
  fetchCommunicationNotificationsSummary,
  markThreadAsRead,
  markAllNotificationsAsRead,
  archiveCommunicationThread,
  deleteThreadMessage,
} from "./communicationApi";

function formatDateTime(value, locale = "en-GB") {
  if (!value) return "";
  try {
    const date = new Date(value);
    return date.toLocaleString(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function formatTime(value, locale = "en-GB") {
  if (!value) return "";
  try {
    return new Date(value).toLocaleTimeString(locale, {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function stableLower(value = "") {
  return String(value || "").toLowerCase();
}

function getParticipantDisplay(participant, t) {
  if (!participant) return t("common.unknown", "Unknown");

  if (participant.display_name) return participant.display_name;

  const first = participant.user_first_name || "";
  const last = participant.user_last_name || "";
  const fullName = `${first} ${last}`.trim();

  if (fullName) return fullName;
  if (participant.organisation_name) return participant.organisation_name;
  if (participant.user_email) return participant.user_email;

  return t(`roles.${(participant.role || "").toLowerCase()}`, participant.role);
}

function getMessageParticipant(message, thread) {
  if (!message || !thread) return null;

  const senderId = message.sender ?? message.sender_id;

  return (
    (thread.participants || []).find(
      (participant) => String(participant.id) === String(senderId),
    ) ||
    (thread.participants || []).find(
      (participant) => participant.role === message.sender_role,
    ) ||
    null
  );
}

function getThreadOtherParticipants(thread, t) {
  const seen = new Set();

  return getExternalParticipants(thread)
    .filter((participant) => {
      const key =
        participant.organisation ||
        participant.user ||
        participant.organisation_name ||
        `${participant.role}-${participant.id}`;

      if (seen.has(key)) {
        return false;
      }

      seen.add(key);

      return true;
    })
    .map((participant) => getParticipantDisplay(participant, t))
    .join(", ");
}

function getMainExternalParticipant(thread) {
  return getExternalParticipants(thread)[0] || null;
}

function isOwnMessage(message, selectedThread) {
  if (
    !message ||
    !selectedThread ||
    selectedThread.viewer_participant_id == null
  ) {
    return false;
  }

  const senderId = message.sender ?? message.sender_id;

  return String(senderId) === String(selectedThread.viewer_participant_id);
}

function isSameMinute(a, b) {
  if (!a || !b) return false;
  const da = new Date(a);
  const db = new Date(b);

  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate() &&
    da.getHours() === db.getHours() &&
    da.getMinutes() === db.getMinutes()
  );
}

function buildMessageGroups(messages, selectedThread) {
  const groups = [];

  messages.forEach((message) => {
    const mine = isOwnMessage(message, selectedThread);

    const senderParticipant = getMessageParticipant(message, selectedThread);

    const senderId = message.sender ?? message.sender_id;

    const lastGroup = groups[groups.length - 1];

    const canAppend =
      lastGroup &&
      lastGroup.isMine === mine &&
      String(lastGroup.senderId) === String(senderId) &&
      isSameMinute(
        lastGroup.items[lastGroup.items.length - 1]?.created_at,
        message.created_at,
      );

    if (canAppend) {
      lastGroup.items.push(message);
    } else {
      groups.push({
        senderId,
        senderParticipant,
        isMine: mine,
        items: [message],
      });
    }
  });

  return groups;
}

function getExternalParticipants(thread) {
  const viewerParticipantId = thread?.viewer_participant_id;

  return (thread?.participants || []).filter((participant) => {
    if (participant.is_active === false) {
      return false;
    }

    if (
      viewerParticipantId != null &&
      String(participant.id) === String(viewerParticipantId)
    ) {
      return false;
    }

    return true;
  });
}

function getThreadActorLabel(thread, t) {
  const externalParticipants = getExternalParticipants(thread);

  const hasLender = externalParticipants.some(
    (participant) => participant.role === "LENDER",
  );

  const hasOss = externalParticipants.some(
    (participant) => participant.role === "OSS",
  );

  if (hasLender && hasOss) {
    return t("actors.project", "Project actors");
  }

  if (hasLender) {
    return t("actors.lender", "Lender");
  }

  if (hasOss) {
    return t("actors.oss", "One-Stop Shop");
  }

  return t("actors.general", "General");
}

function getThreadActorClass(thread) {
  const externalParticipants = getExternalParticipants(thread);

  const hasLender = externalParticipants.some(
    (participant) => participant.role === "LENDER",
  );

  const hasOss = externalParticipants.some(
    (participant) => participant.role === "OSS",
  );

  if (hasLender && hasOss) {
    return "is-project";
  }

  if (hasLender) {
    return "is-lender";
  }

  if (hasOss) {
    return "is-oss";
  }

  return "is-general";
}

function getParticipantAvatar(participant) {
  return participant?.avatar_url || "";
}

function getParticipantInitials(participant, t) {
  const name = getParticipantDisplay(participant, t);

  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function ParticipantAvatar({
  participant,
  fallbackName = "",
  size = "message",
  t,
}) {
  const [imageFailed, setImageFailed] = React.useState(false);

  const displayName = participant
    ? getParticipantDisplay(participant, t)
    : fallbackName || t("common.unknown", "Unknown");

  const initials = participant
    ? getParticipantInitials(participant, t)
    : displayName
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0))
        .join("")
        .toUpperCase() || "?";

  const avatarUrl = participant?.avatar_url || "";

  return (
    <div
      className={`hb-comm-avatar hb-comm-avatar--${size}`}
      title={displayName}
      aria-label={displayName}
    >
      {avatarUrl && !imageFailed ? (
        <img
          src={avatarUrl}
          alt={displayName}
          className="hb-comm-avatar__image"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <span className="hb-comm-avatar__fallback">{initials}</span>
      )}
    </div>
  );
}

export default function HomebuyerCommunicationPage({ dossierId = null }) {
  const { t, i18n } = useTranslation("homebuyer-communication");
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const [threads, setThreads] = useState([]);
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [notificationsUnread, setNotificationsUnread] = useState(0);

  const [searchTerm, setSearchTerm] = useState("");
  const [messageBody, setMessageBody] = useState("");

  const [isLoadingThreads, setIsLoadingThreads] = useState(true);
  const [isLoadingThreadDetail, setIsLoadingThreadDetail] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false);

  const [deleteModal, setDeleteModal] = useState({
    open: false,
    threadId: null,
    title: "",
  });

  const [feedbackModal, setFeedbackModal] = useState({
    open: false,
    type: "",
    title: "",
    text: "",
  });

  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [lastMessageId, setLastMessageId] = useState(null);
  const [firstUnreadMessageId, setFirstUnreadMessageId] = useState(null);

  const messagesContainerRef = useRef(null);
  const stayReadTimerRef = useRef(null);
  const scrollReadTimerRef = useRef(null);
  const isMarkingThreadReadRef = useRef(false);
  const previousSelectedThreadIdRef = useRef(null);

  const locale =
    i18n.language === "es"
      ? "es-ES"
      : i18n.language === "ca"
        ? "ca-ES"
        : i18n.language === "nl"
          ? "nl-BE"
          : "en-GB";

  const selectedThreadUnreadCount =
    threads.find((thread) => thread.id === selectedThreadId)?.unread_count || 0;

  const closeFeedbackModal = () => {
    setFeedbackModal({
      open: false,
      type: "",
      title: "",
      text: "",
    });
  };

  async function getAccessToken() {
    return getAccessTokenSilently();
  }

  function isNearBottom(container) {
    if (!container) return false;
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    return distanceFromBottom <= 120;
  }

  function scrollToBottom(behavior = "smooth") {
    const container = messagesContainerRef.current;
    if (!container) return;

    container.scrollTo({
      top: container.scrollHeight,
      behavior,
    });
  }

  async function loadNotificationsSummary() {
    try {
      const accessToken = await getAccessToken();
      const data = await fetchCommunicationNotificationsSummary({
        accessToken,
      });
      setNotificationsUnread(data?.unread_count || 0);
    } catch {
      // silent
    }
  }

  async function loadThreads(options = {}) {
    const { silent = false } = options;

    try {
      if (!silent) {
        setIsLoadingThreads(true);
      }

      const accessToken = await getAccessToken();

      const data = dossierId
        ? await fetchDossierCommunicationThreads({
            accessToken,
            dossierId,
          })
        : await fetchCommunicationThreads({ accessToken });

      const nextThreads = Array.isArray(data) ? data : [];
      setThreads(nextThreads);

      if (nextThreads.length > 0) {
        setSelectedThreadId((prev) =>
          prev && nextThreads.some((thread) => thread.id === prev)
            ? prev
            : nextThreads[0].id,
        );
      } else {
        setSelectedThreadId(null);
        setSelectedThread(null);
        setMessages([]);
        setFirstUnreadMessageId(null);
        setShowScrollToBottom(false);
      }

      await loadNotificationsSummary();
    } catch (error) {
      if (!silent) {
        setFeedbackModal({
          open: true,
          type: "error",
          title: t("messages.errorTitle", "Something went wrong"),
          text:
            error.message ||
            t(
              "messages.loadThreadsError",
              "Could not load communication threads.",
            ),
        });
      } else {
        console.error("Silent thread list refresh failed:", error);
      }
    } finally {
      if (!silent) {
        setIsLoadingThreads(false);
      }
    }
  }

  async function loadThreadDetail(threadId, options = {}) {
    if (!threadId) return;

    const { silent = false } = options;

    try {
      if (!silent) {
        setIsLoadingThreadDetail(true);
      }

      const container = messagesContainerRef.current;
      const wasNearBottom = isNearBottom(container);
      const previousScrollTop = container ? container.scrollTop : 0;
      const previousScrollHeight = container ? container.scrollHeight : 0;

      const currentThread = threads.find((thread) => thread.id === threadId);
      const unreadCount = currentThread?.unread_count || 0;

      const accessToken = await getAccessToken();
      const data = await fetchCommunicationThreadDetail({
        accessToken,
        threadId,
      });

      const nextMessages = Array.isArray(data?.messages) ? data.messages : [];
      const nextLastMessageId =
        nextMessages.length > 0
          ? nextMessages[nextMessages.length - 1].id
          : null;

      const hadMessagesBefore = messages.length > 0;
      const hasNewMessage =
        hadMessagesBefore &&
        nextLastMessageId &&
        lastMessageId &&
        nextLastMessageId !== lastMessageId;

      let firstUnreadId = null;
      if (unreadCount > 0 && nextMessages.length >= unreadCount) {
        const unreadSlice = nextMessages.slice(-unreadCount);
        firstUnreadId = unreadSlice[0]?.id || null;
      }

      setSelectedThread(data);
      setMessages(nextMessages);
      setLastMessageId(nextLastMessageId);
      setFirstUnreadMessageId(firstUnreadId);

      requestAnimationFrame(() => {
        const currentContainer = messagesContainerRef.current;
        if (!currentContainer) return;

        if (!hadMessagesBefore) {
          currentContainer.scrollTop = 0;
          setShowScrollToBottom(
            unreadCount > 0 || !isNearBottom(currentContainer),
          );
          return;
        }

        if (wasNearBottom) {
          currentContainer.scrollTop = currentContainer.scrollHeight;
          setShowScrollToBottom(false);
          return;
        }

        const newScrollHeight = currentContainer.scrollHeight;
        const heightDiff = newScrollHeight - previousScrollHeight;
        currentContainer.scrollTop = previousScrollTop + heightDiff;

        if (hasNewMessage) {
          setShowScrollToBottom(true);
        } else {
          setShowScrollToBottom(!isNearBottom(currentContainer));
        }
      });
    } catch (error) {
      if (!silent) {
        setFeedbackModal({
          open: true,
          type: "error",
          title: t("messages.errorTitle", "Something went wrong"),
          text:
            error.message ||
            t(
              "messages.loadThreadError",
              "Could not load the selected thread.",
            ),
        });
      } else {
        console.error("Silent thread refresh failed:", error);
      }
    } finally {
      if (!silent) {
        setIsLoadingThreadDetail(false);
      }
    }
  }

  async function handleMarkSelectedThreadAsRead(trigger = "stay") {
    if (!selectedThreadId || !selectedThread) return;
    if (isMarkingThreadReadRef.current) return;

    const currentThread = threads.find(
      (thread) => thread.id === selectedThreadId,
    );
    const unreadCount = currentThread?.unread_count || 0;

    if (unreadCount <= 0) return;

    const container = messagesContainerRef.current;
    if (!isNearBottom(container)) return;

    try {
      isMarkingThreadReadRef.current = true;

      const accessToken = await getAccessToken();
      await markThreadAsRead({ accessToken, threadId: selectedThreadId });

      setThreads((prev) =>
        prev.map((thread) =>
          thread.id === selectedThreadId
            ? {
                ...thread,
                unread_count: 0,
              }
            : thread,
        ),
      );

      setFirstUnreadMessageId(null);
      setShowScrollToBottom(false);
      await loadNotificationsSummary();
    } catch (error) {
      console.error(`Failed to mark thread as read via ${trigger}:`, error);
    } finally {
      isMarkingThreadReadRef.current = false;
    }
  }

  useEffect(() => {
    if (!isAuthenticated) return;
    loadThreads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, dossierId]);

  useEffect(() => {
    if (!selectedThreadId || !isAuthenticated) return;

    if (previousSelectedThreadIdRef.current !== selectedThreadId) {
      previousSelectedThreadIdRef.current = selectedThreadId;
      setShowScrollToBottom(false);
      setFirstUnreadMessageId(null);
    }

    if (stayReadTimerRef.current) {
      clearTimeout(stayReadTimerRef.current);
    }

    if (scrollReadTimerRef.current) {
      clearTimeout(scrollReadTimerRef.current);
    }

    loadThreadDetail(selectedThreadId);

    return () => {
      if (stayReadTimerRef.current) {
        clearTimeout(stayReadTimerRef.current);
      }
      if (scrollReadTimerRef.current) {
        clearTimeout(scrollReadTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedThreadId, isAuthenticated]);

  useEffect(() => {
    if (!selectedThreadId || !selectedThread || isLoadingThreadDetail) return;
    if (selectedThreadUnreadCount <= 0) return;

    if (stayReadTimerRef.current) {
      clearTimeout(stayReadTimerRef.current);
    }

    stayReadTimerRef.current = setTimeout(() => {
      handleMarkSelectedThreadAsRead("stay");
    }, 5500);

    return () => {
      if (stayReadTimerRef.current) {
        clearTimeout(stayReadTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedThreadId,
    selectedThreadUnreadCount,
    selectedThread,
    isLoadingThreadDetail,
  ]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || !selectedThreadId || !selectedThread) return;

    function handleMessagesScroll() {
      const nearBottom = isNearBottom(container);

      if (nearBottom) {
        setShowScrollToBottom(false);
      } else {
        setShowScrollToBottom(true);
      }

      if (!nearBottom) return;
      if (selectedThreadUnreadCount <= 0) return;

      if (scrollReadTimerRef.current) {
        clearTimeout(scrollReadTimerRef.current);
      }

      scrollReadTimerRef.current = setTimeout(() => {
        handleMarkSelectedThreadAsRead("scroll");
      }, 2500);
    }

    container.addEventListener("scroll", handleMessagesScroll, {
      passive: true,
    });

    handleMessagesScroll();

    return () => {
      container.removeEventListener("scroll", handleMessagesScroll);
      if (scrollReadTimerRef.current) {
        clearTimeout(scrollReadTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedThreadId, selectedThread, selectedThreadUnreadCount]);

  const filteredThreads = useMemo(() => {
    return threads.filter((thread) => {
      const haystack = [
        thread.title,
        getThreadOtherParticipants(thread, t),
        thread.latest_message?.body,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(searchTerm.trim().toLowerCase());
    });
  }, [threads, searchTerm, t]);

  async function handleSendMessage(event) {
    event.preventDefault();

    const trimmed = messageBody.trim();
    if (!trimmed || !selectedThreadId) return;

    try {
      setIsSendingMessage(true);

      const accessToken = await getAccessToken();

      await createThreadMessage({
        accessToken,
        threadId: selectedThreadId,
        body: trimmed,
        messageType: "COMMENT",
        visibility: "ALL",
      });

      setMessageBody("");
      await loadThreadDetail(selectedThreadId, { silent: true });
      await loadThreads({ silent: true });
      setShowScrollToBottom(true);
    } catch (error) {
      setFeedbackModal({
        open: true,
        type: "error",
        title: t("messages.errorTitle", "Something went wrong"),
        text:
          error.message ||
          t("messages.sendError", "Could not send your message."),
      });
    } finally {
      setIsSendingMessage(false);
    }
  }

  function handleComposerKeyDown(event) {
    const isSubmitShortcut =
      event.key === "Enter" && (event.metaKey || event.ctrlKey);

    if (!isSubmitShortcut) return;

    if (event.nativeEvent?.isComposing) return;

    event.preventDefault();

    if (!messageBody.trim() || isSendingMessage) return;

    handleSendMessage(event);
  }

  async function handleMarkAllNotificationsRead() {
    try {
      setIsMarkingAllRead(true);

      const accessToken = await getAccessToken();
      await markAllNotificationsAsRead({ accessToken });

      setNotificationsUnread(0);
      setThreads((prev) =>
        prev.map((thread) => ({
          ...thread,
          unread_count: 0,
        })),
      );
      setFirstUnreadMessageId(null);
    } catch (error) {
      setFeedbackModal({
        open: true,
        type: "error",
        title: t("messages.errorTitle", "Something went wrong"),
        text:
          error.message ||
          t(
            "messages.markAllReadError",
            "Could not mark notifications as read.",
          ),
      });
    } finally {
      setIsMarkingAllRead(false);
    }
  }

  async function confirmDeleteThread() {
    if (!deleteModal.threadId) return;

    try {
      const accessToken = await getAccessToken();

      await archiveCommunicationThread({
        accessToken,
        threadId: deleteModal.threadId,
      });

      const updatedThreads = threads.filter(
        (thread) => thread.id !== deleteModal.threadId,
      );

      setThreads(updatedThreads);

      if (selectedThreadId === deleteModal.threadId) {
        if (updatedThreads.length > 0) {
          setSelectedThreadId(updatedThreads[0].id);
        } else {
          setSelectedThreadId(null);
          setSelectedThread(null);
          setMessages([]);
        }
      }

      setDeleteModal({
        open: false,
        threadId: null,
        title: "",
      });

      setFeedbackModal({
        open: true,
        type: "success",
        title: t("messages.successTitle", "Success"),
        text: t(
          "messages.threadDeleted",
          "The conversation was deleted successfully.",
        ),
      });

      await loadNotificationsSummary();
    } catch (error) {
      setDeleteModal({
        open: false,
        threadId: null,
        title: "",
      });

      setFeedbackModal({
        open: true,
        type: "error",
        title: t("messages.errorTitle", "Something went wrong"),
        text:
          error.message ||
          t("messages.deleteThreadError", "Could not delete the conversation."),
      });
    }
  }

  useEffect(() => {
    if (!selectedThreadId || !isAuthenticated) return;

    const interval = setInterval(async () => {
      if (document.visibilityState !== "visible") return;

      try {
        await loadThreadDetail(selectedThreadId, { silent: true });
        await loadThreads({ silent: true });
      } catch (error) {
        console.error("Polling communication thread failed:", error);
      }
    }, 15000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedThreadId, isAuthenticated]);

  function handleScrollToUnreadOrBottom() {
    if (firstUnreadMessageId) {
      const target = document.getElementById(
        `hb-comm-message-anchor-${firstUnreadMessageId}`,
      );
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }

    scrollToBottom("smooth");
  }

  const selectedMainParticipant = getMainExternalParticipant(selectedThread);

  const selectedAvatarUrl = getParticipantAvatar(selectedMainParticipant);

  const selectedAvatarInitials = getParticipantInitials(
    selectedMainParticipant,

    t,
  );

  const [messageDeleteModal, setMessageDeleteModal] = useState({
    open: false,
    messageId: null,
  });

  const [isDeletingMessage, setIsDeletingMessage] = useState(false);

  async function confirmDeleteMessage() {
    if (!messageDeleteModal.messageId || !selectedThreadId) {
      return;
    }

    try {
      setIsDeletingMessage(true);

      const accessToken = await getAccessToken();

      await deleteThreadMessage({
        accessToken,
        threadId: selectedThreadId,
        messageId: messageDeleteModal.messageId,
      });

      setMessageDeleteModal({
        open: false,
        messageId: null,
      });

      await loadThreadDetail(selectedThreadId, {
        silent: true,
      });

      await loadThreads({
        silent: true,
      });
    } catch (error) {
      setMessageDeleteModal({
        open: false,
        messageId: null,
      });

      setFeedbackModal({
        open: true,
        type: "error",
        title: t("messages.errorTitle", "Something went wrong"),
        text:
          error.message ||
          t("messages.deleteMessageError", "The message could not be deleted."),
      });
    } finally {
      setIsDeletingMessage(false);
    }
  }

  return (
    <>
      <div className="hb-comm-page">
        <div className="hb-comm-page__shell">
          <header className="hb-comm-hero">
            <div>
              <p className="hb-comm-hero__eyebrow">
                {t("hero.eyebrow", "Communication hub")}
              </p>
              <h1 className="hb-comm-hero__title">
                {t("hero.title", "Messages & coordination")}
              </h1>
              <p className="hb-comm-hero__subtitle">
                {t(
                  "hero.subtitle",
                  "Communicate with your One-Stop Shop and lender in one place. Keep track of questions, updates and project-related discussions.",
                )}
              </p>
            </div>

            <div className="hb-comm-hero__meta">
              <div className="hb-comm-hero__meta-card">
                <span>{t("hero.totalThreads", "Threads")}</span>
                <strong>{threads.length}</strong>
              </div>

              <div className="hb-comm-hero__meta-card">
                <span>{t("hero.unreadNotifications", "Unread")}</span>
                <strong>{notificationsUnread}</strong>
              </div>

              <button
                type="button"
                className="hb-comm-hero__meta-btn"
                onClick={handleMarkAllNotificationsRead}
                disabled={isMarkingAllRead || notificationsUnread === 0}
              >
                <Bell size={15} />
                {isMarkingAllRead
                  ? t("buttons.marking", "Marking...")
                  : t("buttons.markAllRead", "Mark all as read")}
              </button>
            </div>
          </header>
          <div className="hb-comm-disclaimer">
            <Info size={16} />
            <p>
              {t(
                "disclaimer.text",
                "You do not need to create conversations manually. Communication threads with your One-Stop Shop and lender are created automatically at the appropriate stage of the process. Your OSS conversation becomes available when the OSS accepts your request or contacts you before acceptance, while your lender conversation becomes available after the lender submits the agreed loan terms and loan decision following your offline meeting.",
              )}
            </p>
          </div>
          <div className="hb-comm-layout">
            <aside className="hb-comm-sidebar">
              <div className="hb-comm-sidebar__top">
                <div className="hb-comm-search">
                  <Search size={16} />
                  <input
                    type="text"
                    placeholder={t(
                      "search.placeholder",
                      "Search conversations...",
                    )}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              <div className="hb-comm-sidebar__list">
                {isLoadingThreads ? (
                  <div className="hb-comm-empty">
                    <Loader2 size={18} className="hb-comm-spin" />
                    <span>
                      {t("messages.loadingThreads", "Loading threads...")}
                    </span>
                  </div>
                ) : filteredThreads.length === 0 ? (
                  <div className="hb-comm-empty">
                    <Inbox size={18} />
                    <span>
                      {t(
                        "messages.noThreads",
                        "No communication threads found.",
                      )}
                    </span>
                  </div>
                ) : (
                  filteredThreads.map((thread) => {
                    const isSelected = selectedThreadId === thread.id;

                    return (
                      <button
                        key={thread.id}
                        type="button"
                        className={`hb-comm-thread-card ${
                          isSelected ? "is-selected" : ""
                        }`}
                        onClick={() => setSelectedThreadId(thread.id)}
                      >
                        <div className="hb-comm-thread-card__top">
                          <div className="hb-comm-thread-card__avatar">
                            {getParticipantAvatar(
                              getMainExternalParticipant(thread),
                            ) ? (
                              <img
                                src={getParticipantAvatar(
                                  getMainExternalParticipant(thread),
                                )}
                                alt={getParticipantDisplay(
                                  getMainExternalParticipant(thread),
                                  t,
                                )}
                              />
                            ) : (
                              <span>
                                {getParticipantInitials(
                                  getMainExternalParticipant(thread),
                                  t,
                                ) || "OS"}
                              </span>
                            )}
                          </div>

                          <div className="hb-comm-thread-card__main">
                            <div>
                              <div className="hb-comm-thread-card__title-row">
                                <span
                                  className={`hb-comm-thread-card__actor-badge ${getThreadActorClass(
                                    thread,
                                  )}`}
                                >
                                  {getThreadActorLabel(thread, t)}
                                </span>
                                <h3 className="hb-comm-thread-card__title">
                                  {thread.title}
                                </h3>

                                {thread.unread_count > 0 ? (
                                  <span className="hb-comm-thread-card__badge">
                                    {thread.unread_count}
                                  </span>
                                ) : null}
                              </div>

                              <p className="hb-comm-thread-card__type">
                                {t(
                                  `threadTypes.${thread.thread_type}`,
                                  thread.thread_type,
                                )}
                              </p>
                            </div>
                          </div>

                          <ChevronRight size={16} />
                        </div>
                        <p className="hb-comm-thread-card__participants">
                          {getThreadOtherParticipants(thread, t) ||
                            t("common.noParticipants", "No participants")}
                        </p>

                        <p className="hb-comm-thread-card__preview">
                          {thread.latest_message?.is_deleted
                            ? t(
                                "messages.deletedMessage",
                                "This message was deleted.",
                              )
                            : thread.latest_message?.body ||
                              t("common.noMessagesYet", "No messages yet.")}
                        </p>

                        <div className="hb-comm-thread-card__bottom">
                          <span className="hb-comm-thread-card__status">
                            {t(
                              `statuses.${thread.status}`,
                              thread.status || "OPEN",
                            )}
                          </span>

                          <span className="hb-comm-thread-card__time">
                            <Clock3 size={12} />
                            {formatDateTime(
                              thread.latest_message?.created_at ||
                                thread.updated_at,
                              locale,
                            )}
                          </span>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </aside>

            <section className="hb-comm-main">
              {!selectedThreadId ? (
                <div className="hb-comm-main__empty">
                  <MessageSquare size={28} />
                  <h2>{t("empty.title", "Select a conversation")}</h2>
                  <p>
                    {t(
                      "empty.text",
                      "Choose a thread from the left to view messages and continue the discussion.",
                    )}
                  </p>
                </div>
              ) : isLoadingThreadDetail ? (
                <div className="hb-comm-main__empty">
                  <Loader2 size={22} className="hb-comm-spin" />
                  <h2>
                    {t("messages.loadingThread", "Loading conversation...")}
                  </h2>
                </div>
              ) : (
                <>
                  <div className="hb-comm-thread-header">
                    <div className="hb-comm-thread-header__top">
                      <div className="hb-comm-thread-header__identity">
                        <div className="hb-comm-thread-header__avatar">
                          {selectedAvatarUrl ? (
                            <img
                              src={selectedAvatarUrl}
                              alt={getParticipantDisplay(
                                selectedMainParticipant,
                                t,
                              )}
                            />
                          ) : (
                            <span>{selectedAvatarInitials || "OS"}</span>
                          )}
                        </div>

                        <div className="hb-comm-thread-header__title-wrap">
                          <div className="hb-comm-thread-header__title-row">
                            <h2 className="hb-comm-thread-header__title">
                              {selectedThread?.title ||
                                t("common.thread", "Thread")}
                            </h2>

                            {selectedThreadUnreadCount > 0 ? (
                              <span className="hb-comm-thread-header__unread-badge">
                                {selectedThreadUnreadCount}
                              </span>
                            ) : null}
                          </div>

                          <div className="hb-comm-thread-header__meta">
                            <span>
                              {t(
                                `threadTypes.${selectedThread?.thread_type}`,
                                selectedThread?.thread_type || "GENERAL",
                              )}
                            </span>
                            <span>•</span>
                            <span>
                              {t(
                                `statuses.${selectedThread?.status}`,
                                selectedThread?.status || "OPEN",
                              )}
                            </span>
                          </div>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="hb-comm-delete-icon-btn"
                        onClick={() =>
                          setDeleteModal({
                            open: true,
                            threadId: selectedThread?.id || null,
                            title: selectedThread?.title || "",
                          })
                        }
                        title={t("buttons.deleteThread", "Delete conversation")}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>

                    <div className="hb-comm-thread-header__participants">
                      {Array.from(
                        new Map(
                          getExternalParticipants(selectedThread).map((p) => {
                            const key =
                              p.organisation_name || p.user_email || p.id;
                            return [key, p];
                          }),
                        ).values(),
                      ).map((participant) => (
                        <div
                          className="hb-comm-thread-header__participant"
                          key={participant.id}
                        >
                          <span className="hb-comm-thread-header__participant-icon">
                            {participant.organisation_name ? (
                              <Building2 size={14} />
                            ) : (
                              <UserCircle2 size={14} />
                            )}
                          </span>
                          <span>{getParticipantDisplay(participant, t)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="hb-comm-messages-wrapper">
                    <div
                      className="hb-comm-messages"
                      ref={messagesContainerRef}
                    >
                      {messages.length === 0 ? (
                        <div className="hb-comm-main__empty hb-comm-main__empty--messages">
                          <Inbox size={22} />
                          <h3>{t("messages.noMessages", "No messages yet")}</h3>
                          <p>
                            {t(
                              "messages.noMessagesText",
                              "Start the conversation by sending your first message below.",
                            )}
                          </p>
                        </div>
                      ) : (
                        buildMessageGroups(messages, selectedThread).map(
                          (group, groupIndex) => (
                            <div
                              key={`group-${groupIndex}-${group.items[0]?.id}`}
                              className={`hb-comm-message-row ${
                                group.isMine
                                  ? "hb-comm-message-row--mine"
                                  : "hb-comm-message-row--other"
                              }`}
                            >
                              {/* =========================================
        OTHER PARTICIPANT AVATAR
        ========================================= */}

                              {!group.isMine ? (
                                <ParticipantAvatar
                                  participant={group.senderParticipant}
                                  fallbackName={
                                    group.items[0]?.sender_display || ""
                                  }
                                  size="message"
                                  t={t}
                                />
                              ) : null}

                              {/* =========================================
        MESSAGE GROUP
        ========================================= */}

                              <div
                                className={`hb-comm-message-group ${
                                  group.isMine
                                    ? "hb-comm-message-group--mine"
                                    : "hb-comm-message-group--other"
                                }`}
                              >
                                {group.items.map((message, itemIndex) => {
                                  const showUnreadSeparator =
                                    firstUnreadMessageId &&
                                    message.id === firstUnreadMessageId &&
                                    !group.isMine;

                                  const isFirst = itemIndex === 0;

                                  const isLast =
                                    itemIndex === group.items.length - 1;

                                  const isDeleted = Boolean(message.is_deleted);

                                  const canDelete =
                                    group.isMine &&
                                    !isDeleted &&
                                    !["SYSTEM", "STATUS_CHANGE"].includes(
                                      message.message_type,
                                    );

                                  return (
                                    <React.Fragment key={message.id}>
                                      {/* =================================
                  NEW MESSAGES SEPARATOR
                  ================================= */}

                                      {showUnreadSeparator ? (
                                        <div className="hb-comm-unread-separator">
                                          <span className="hb-comm-unread-separator__line" />

                                          <span className="hb-comm-unread-separator__label">
                                            {t(
                                              "messages.newMessages",
                                              "New messages",
                                            )}
                                          </span>

                                          <span className="hb-comm-unread-separator__line" />
                                        </div>
                                      ) : null}

                                      {/* =================================
                  MESSAGE
                  ================================= */}

                                      <div
                                        id={`hb-comm-message-anchor-${message.id}`}
                                        className={`hb-comm-message ${
                                          group.isMine
                                            ? "hb-comm-message--mine"
                                            : "hb-comm-message--other"
                                        } ${
                                          isFirst
                                            ? "hb-comm-message--group-first"
                                            : ""
                                        } ${
                                          isLast
                                            ? "hb-comm-message--group-last"
                                            : ""
                                        } ${
                                          !isFirst && !isLast
                                            ? "hb-comm-message--group-middle"
                                            : ""
                                        } ${
                                          group.items.length === 1
                                            ? "hb-comm-message--single"
                                            : ""
                                        }`}
                                      >
                                        {isFirst ? (
                                          <div className="hb-comm-message__meta">
                                            <span className="hb-comm-message__sender">
                                              {message.sender_display ||
                                                t("common.unknown", "Unknown")}
                                            </span>

                                            <span className="hb-comm-message__type">
                                              {t(
                                                `messageTypes.${message.message_type}`,
                                                message.message_type,
                                              )}
                                            </span>

                                            <span className="hb-comm-message__time">
                                              {formatTime(
                                                message.created_at,
                                                locale,
                                              )}
                                            </span>
                                          </div>
                                        ) : null}

                                        <div
                                          className={`hb-comm-message__body ${
                                            isDeleted
                                              ? "hb-comm-message__body--deleted"
                                              : ""
                                          }`}
                                        >
                                          {isDeleted
                                            ? group.isMine
                                              ? t(
                                                  "messages.youDeletedMessage",
                                                  "You deleted this message.",
                                                )
                                              : t(
                                                  "messages.deletedMessage",
                                                  "This message was deleted.",
                                                )
                                            : message.body ||
                                              t(
                                                "common.emptyMessage",
                                                "Empty message",
                                              )}
                                        </div>

                                        {canDelete ? (
                                          <button
                                            type="button"
                                            className="hb-comm-message__delete-btn"
                                            onClick={() =>
                                              setMessageDeleteModal({
                                                open: true,
                                                messageId: message.id,
                                              })
                                            }
                                            title={t(
                                              "buttons.deleteMessage",
                                              "Delete message",
                                            )}
                                            aria-label={t(
                                              "buttons.deleteMessage",
                                              "Delete message",
                                            )}
                                          >
                                            <Trash2 size={13} />
                                          </button>
                                        ) : null}

                                        {!isFirst ? (
                                          <div className="hb-comm-message__time hb-comm-message__time--inline">
                                            {formatTime(
                                              message.created_at,
                                              locale,
                                            )}
                                          </div>
                                        ) : null}
                                      </div>
                                    </React.Fragment>
                                  );
                                })}
                              </div>

                              {group.isMine ? (
                                <ParticipantAvatar
                                  participant={group.senderParticipant}
                                  fallbackName={
                                    group.items[0]?.sender_display || ""
                                  }
                                  size="message"
                                  t={t}
                                />
                              ) : null}
                            </div>
                          ),
                        )
                      )}
                    </div>

                    {showScrollToBottom ? (
                      <button
                        type="button"
                        className="hb-comm-scroll-to-bottom"
                        onClick={handleScrollToUnreadOrBottom}
                      >
                        <ArrowDown size={15} />
                        {selectedThreadUnreadCount > 0
                          ? t("buttons.scrollToUnread", "Go to new messages")
                          : t("buttons.scrollToLatest", "Go to latest")}
                      </button>
                    ) : null}
                  </div>

                  <form
                    className="hb-comm-composer"
                    onSubmit={handleSendMessage}
                  >
                    <textarea
                      rows="3"
                      value={messageBody}
                      onChange={(e) => setMessageBody(e.target.value)}
                      onKeyDown={handleComposerKeyDown}
                      placeholder={t(
                        "composer.placeholder",
                        "Write your message here...",
                      )}
                    />

                    <div className="hb-comm-composer__actions">
                      <span className="hb-comm-composer__hint">
                        {t(
                          "composer.hint",
                          "Messages sent here are visible to the relevant actors in this thread.",
                        )}
                      </span>

                      <button
                        type="submit"
                        className="hb-comm-send-btn"
                        disabled={isSendingMessage || !messageBody.trim()}
                      >
                        {isSendingMessage ? (
                          <>
                            <Loader2 size={15} className="hb-comm-spin" />
                            {t("buttons.sending", "Sending...")}
                          </>
                        ) : (
                          <>
                            <Send size={15} />
                            {t("buttons.send", "Send")}
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </>
              )}
            </section>
          </div>
        </div>
      </div>

      {feedbackModal.open ? (
        <div className="hb-comm-modal-overlay" onClick={closeFeedbackModal}>
          <div
            className={`hb-comm-modal ${
              feedbackModal.type === "error"
                ? "hb-comm-modal--error"
                : "hb-comm-modal--success"
            }`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="hb-comm-modal__icon">
              {feedbackModal.type === "error" ? (
                <AlertCircle size={22} />
              ) : (
                <CheckCircle2 size={22} />
              )}
            </div>

            <div className="hb-comm-modal__content">
              <h3>{feedbackModal.title}</h3>
              <p>{feedbackModal.text}</p>
            </div>

            <button
              type="button"
              className="hb-comm-modal__close"
              onClick={closeFeedbackModal}
              aria-label={t("common.close", "Close")}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      ) : null}

      {deleteModal.open ? (
        <div
          className="hb-comm-modal-overlay"
          onClick={() =>
            setDeleteModal({
              open: false,
              threadId: null,
              title: "",
            })
          }
        >
          <div
            className="hb-comm-delete-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="hb-comm-delete-modal__icon">
              <Trash2 size={20} />
            </div>

            <div className="hb-comm-delete-modal__content">
              <h3>{t("deleteModal.title", "Delete conversation?")}</h3>
              <p>
                {t(
                  "deleteModal.text",
                  "This conversation will be permanently removed.",
                )}
              </p>
              {deleteModal.title ? (
                <div className="hb-comm-delete-modal__thread-name">
                  {deleteModal.title}
                </div>
              ) : null}
            </div>

            <div className="hb-comm-delete-modal__actions">
              <button
                type="button"
                className="hb-comm-delete-modal__cancel"
                onClick={() =>
                  setDeleteModal({
                    open: false,
                    threadId: null,
                    title: "",
                  })
                }
              >
                {t("buttons.cancel", "Cancel")}
              </button>

              <button
                type="button"
                className="hb-comm-delete-modal__confirm"
                onClick={confirmDeleteThread}
              >
                <Trash2 size={15} />
                {t("buttons.delete", "Delete")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {messageDeleteModal.open ? (
        <div
          className="hb-comm-modal-overlay"
          onClick={() => {
            if (isDeletingMessage) {
              return;
            }

            setMessageDeleteModal({
              open: false,
              messageId: null,
            });
          }}
        >
          <div
            className="hb-comm-delete-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="hb-comm-delete-modal__icon">
              <Trash2 size={20} />
            </div>

            <div className="hb-comm-delete-modal__content">
              <h3>{t("deleteMessageModal.title", "Delete message?")}</h3>

              <p>
                {t(
                  "deleteMessageModal.text",
                  "The message will remain in the conversation as deleted, but its content will no longer be visible.",
                )}
              </p>
            </div>

            <div className="hb-comm-delete-modal__actions">
              <button
                type="button"
                className="hb-comm-delete-modal__cancel"
                onClick={() =>
                  setMessageDeleteModal({
                    open: false,
                    messageId: null,
                  })
                }
                disabled={isDeletingMessage}
              >
                {t("buttons.cancel", "Cancel")}
              </button>

              <button
                type="button"
                className="hb-comm-delete-modal__confirm"
                onClick={confirmDeleteMessage}
                disabled={isDeletingMessage}
              >
                {isDeletingMessage ? (
                  <Loader2 size={15} className="hb-comm-spin" />
                ) : (
                  <Trash2 size={15} />
                )}

                {isDeletingMessage
                  ? t("buttons.deleting", "Deleting...")
                  : t("buttons.delete", "Delete")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
